import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Save, ArrowLeft, Upload, Image, X, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'programming', label: 'Programming', label_tr: 'Programlama' },
  { id: 'design', label: 'Design', label_tr: 'Tasarım' },
  { id: 'business', label: 'Business', label_tr: 'İş Dünyası' },
  { id: 'marketing', label: 'Marketing', label_tr: 'Pazarlama' },
  { id: 'music', label: 'Music', label_tr: 'Müzik' },
  { id: 'photography', label: 'Photography', label_tr: 'Fotoğraf' },
  { id: 'language', label: 'Languages', label_tr: 'Diller' },
  { id: 'health', label: 'Health', label_tr: 'Sağlık' },
];

export default function InstructorCourseEdit() {
  const { courseId } = useParams();
  const isNew = !courseId || courseId === 'new';
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    title: '', title_tr: '', description: '', description_tr: '',
    category: 'programming', price: 0, currency: 'TRY', thumbnail: '',
    vimeo_intro_url: '', level: 'beginner', language: 'tr', is_interactive: false
  });

  useEffect(() => {
    if (!isNew) {
      axios.get(`${API}/courses/${courseId}`).then(res => {
        const c = res.data.course;
        setForm({
          title: c.title || '', title_tr: c.title_tr || '', description: c.description || '',
          description_tr: c.description_tr || '', category: c.category || 'programming',
          price: c.price || 0, currency: c.currency || 'TRY', thumbnail: c.thumbnail || '',
          vimeo_intro_url: c.vimeo_intro_url || '', level: c.level || 'beginner',
          language: c.language || 'tr', is_interactive: c.is_interactive || false
        });
        if (c.thumbnail) setThumbnailPreview(c.thumbnail);
      }).catch(console.error);
    }
  }, [courseId, isNew]);

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const localPreview = URL.createObjectURL(file);
    setThumbnailPreview(localPreview);
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/thumbnail`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm(prev => ({ ...prev, thumbnail: fileUrl }));
      setThumbnailPreview(fileUrl);
    } catch (err) {
      console.error('Upload error:', err);
      setThumbnailPreview('');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/instructor/courses`, form, { headers: authHeaders() });
      } else {
        await axios.put(`${API}/instructor/courses/${courseId}`, form, { headers: authHeaders() });
      }
      navigate('/instructor');
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <div className="min-h-screen bg-[var(--bg-app)] py-8" data-testid="instructor-course-edit-page">
      <div className="max-w-3xl mx-auto px-4">
        <button onClick={() => navigate('/instructor')} className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text-main)] mb-6 font-bold text-sm" data-testid="back-btn">
          <ArrowLeft size={18} /> {lang === 'tr' ? 'Geri' : 'Back'}
        </button>

        <h1 className="font-display text-3xl font-bold mb-8">
          {isNew ? (lang === 'tr' ? 'Yeni Kurs Oluştur' : 'Create New Course') : (lang === 'tr' ? 'Kurs Düzenle' : 'Edit Course')}
        </h1>

        <div className="space-y-6">
          {/* Thumbnail Upload */}
          <div className="card-clay p-6">
            <h3 className="font-display font-bold text-lg mb-4">{lang === 'tr' ? 'Kurs Görseli' : 'Course Thumbnail'}</h3>
            <div className="flex items-start gap-6">
              <div className="w-48 h-28 rounded-xl border-2 border-dashed border-[var(--border-light)] bg-gray-50 overflow-hidden flex items-center justify-center relative">
                {thumbnailPreview ? (
                  <>
                    <img src={thumbnailPreview} alt="Thumbnail" className="w-full h-full object-cover" />
                    <button onClick={() => { setThumbnailPreview(''); updateField('thumbnail', ''); }} className="absolute top-1 right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow">
                      <X size={12} />
                    </button>
                  </>
                ) : (
                  <Image size={32} className="text-[var(--text-muted)]" />
                )}
              </div>
              <div>
                <input type="file" ref={fileInputRef} accept="image/*" onChange={handleThumbnailUpload} className="hidden" />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="btn-tactile px-4 py-2 text-sm bg-white border-[var(--border-light)] flex items-center gap-2" data-testid="upload-thumbnail-btn">
                  <Upload size={16} /> {uploading ? (lang === 'tr' ? 'Yükleniyor...' : 'Uploading...') : (lang === 'tr' ? 'Görsel Yükle' : 'Upload Image')}
                </button>
                <p className="text-xs text-[var(--text-muted)] mt-2">{lang === 'tr' ? 'JPG, PNG, WebP - Maks 5MB' : 'JPG, PNG, WebP - Max 5MB'}</p>
                {form.thumbnail && (
                  <div className="flex items-center gap-1 mt-2 text-[var(--brand-success)] text-xs font-bold">
                    <CheckCircle size={14} /> {lang === 'tr' ? 'Görsel yüklendi' : 'Image uploaded'}
                  </div>
                )}
                <div className="mt-2">
                  <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'veya URL yapistirin' : 'or paste URL'}</label>
                  <input type="text" value={form.thumbnail} onChange={e => { updateField('thumbnail', e.target.value); if (e.target.value) setThumbnailPreview(e.target.value); }}
                    placeholder="https://..."
                    className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-xs"
                    data-testid="thumbnail-url-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Course Info */}
          <div className="card-clay p-6 space-y-4">
            <h3 className="font-display font-bold text-lg">{lang === 'tr' ? 'Kurs Bilgileri' : 'Course Information'}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">Title (EN)</label>
                <input type="text" value={form.title} onChange={e => updateField('title', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold"
                  placeholder="e.g. Python Programming Masterclass" data-testid="course-title-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">Baslik (TR)</label>
                <input type="text" value={form.title_tr} onChange={e => updateField('title_tr', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold"
                  placeholder="orn. Python Programlama Ustaligi" data-testid="course-title-tr-input" />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">Description (EN)</label>
              <textarea value={form.description} onChange={e => updateField('description', e.target.value)} rows={3}
                className="w-full px-4 py-3 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold"
                data-testid="course-desc-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">Açıklama (TR)</label>
              <textarea value={form.description_tr} onChange={e => updateField('description_tr', e.target.value)} rows={3}
                className="w-full px-4 py-3 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold"
                data-testid="course-desc-tr-input" />
            </div>
          </div>

          {/* Settings */}
          <div className="card-clay p-6 space-y-4">
            <h3 className="font-display font-bold text-lg">{lang === 'tr' ? 'Kurs Ayarları' : 'Course Settings'}</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Kategori' : 'Category'}</label>
                <select value={form.category} onChange={e => updateField('category', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold"
                  data-testid="course-category-select">
                  {categories.map(c => <option key={c.id} value={c.id}>{lang === 'tr' ? c.label_tr : c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Seviye' : 'Level'}</label>
                <select value={form.level} onChange={e => updateField('level', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold"
                  data-testid="course-level-select">
                  <option value="beginner">{lang === 'tr' ? 'Başlangıç' : 'Beginner'}</option>
                  <option value="intermediate">{lang === 'tr' ? 'Orta' : 'Intermediate'}</option>
                  <option value="advanced">{lang === 'tr' ? 'İleri' : 'Advanced'}</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Fiyat (TRY)' : 'Price (TRY)'}</label>
                <input type="number" value={form.price} onChange={e => updateField('price', parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold"
                  data-testid="course-price-input" />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Tanitim Videosu (Vimeo URL)' : 'Intro Video (Vimeo URL)'}</label>
              <input type="text" value={form.vimeo_intro_url} onChange={e => updateField('vimeo_intro_url', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold"
                placeholder="https://player.vimeo.com/video/..." data-testid="course-vimeo-input" />
              <p className="text-xs text-[var(--text-muted)] mt-1">{lang === 'tr' ? 'Vimeo embed URL\'sini yapistirin' : 'Paste Vimeo embed URL'}</p>
            </div>

            <div className="flex items-center gap-3 p-4 bg-[#FFF4CC] rounded-xl">
              <input type="checkbox" checked={form.is_interactive} onChange={e => updateField('is_interactive', e.target.checked)}
                className="w-5 h-5 rounded accent-[var(--brand-secondary)]" data-testid="course-interactive-check" />
              <div>
                <label className="font-bold text-sm">{lang === 'tr' ? 'Interaktif Kurs' : 'Interactive Course'}</label>
                <p className="text-xs text-[var(--text-muted)]">{lang === 'tr' ? 'Duolingo tarzi AI quiz ve interaktif alistirmalar ekle' : 'Add Duolingo-style AI quizzes and interactive exercises'}</p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={saving || !form.title}
            className="w-full btn-success py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="save-course-btn"
          >
            <Save size={20} /> {saving ? (lang === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (lang === 'tr' ? 'Kursu Kaydet' : 'Save Course')}
          </motion.button>
        </div>
      </div>
    </div>
  );
}
